export const separateDecimal = (value) => {
  if (value === "N/A") {
    return {
      whole: "N/A",
      decimal: "",
    };
  }

  if (value.includes(",")) {
    const removeCommas = value.split(",").join("");

    const parts = removeCommas.split(".");

    return {
      whole: parts[0],
      decimal: parts[1],
    };
  } else {
    const number = parseFloat(value);

    if (isNaN(number)) {
      throw new Error("Invalid number");
    }
  }

  const parts = value.split(".");

  return {
    whole: parts[0],
    decimal: parts[1],
  };
};

export function separateNumberWithoutFormat(value) {
  // 1) N/A special case
  if (value === "N/A") {
    return { whole: "N/A", decimal: "", hasDecimal: false };
  }

  // 2) Normalize input to a cleaned string
  let raw = value;
  if (typeof raw === "number") {
    raw = String(raw);
  }

  if (typeof raw !== "string") {
    throw new Error(`separateNumberParts: unsupported type ${typeof raw}`);
  }

  // 3) Strip commas & trim
  const cleaned = raw.replace(/,/g, "").trim();

  // 4) Handle empty → treat as zero (no decimal)
  if (cleaned === "") {
    return { whole: "0", decimal: "", hasDecimal: false };
  }

  // 5) Validate it’s a number
  const n = parseFloat(cleaned);
  if (isNaN(n)) {
    throw new Error(`separateNumberParts: "${value}" is not a valid number`);
  }

  // 6) Split on the dot **without** re‑formatting
  const [whole, decimal = ""] = cleaned.split(".");

  // 7) Only consider it “hasDecimal” if there was a non‑empty string _after_ the dot
  const hasDecimal = decimal.length > 0;

  return { whole, decimal, hasDecimal };
}

export const separateNumberParts = (value) => {
  if (value === "N/A") {
    return { whole: "N/A", decimal: "", hasDecimal: false };
  }

  let n;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim();
    // treat empty or purely whitespace as zero
    if (cleaned === "") {
      n = 0;
    } else {
      n = parseFloat(cleaned);
    }
  } else if (typeof value === "number") {
    n = value;
  } else {
    throw new Error(`separateDecimal: unsupported type ${typeof value}`);
  }

  if (isNaN(n)) {
    throw new Error(`separateDecimal: "${value}" is not a valid number`);
  }

  const formatted = format4(n);

  const [whole, decimal = ""] = formatted.split(".");
  const hasDecimal = decimal !== "0000";

  return { whole, decimal, hasDecimal };
};



export const numberWithCommas = (amount) => {
  if (amount === "NaN" || isNaN(amount) || amount === "" || amount === undefined || amount === null) {
    return "0";
  } else {
    return amount.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }
};

const SMALL_NUMBERS = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const SCALE = [
  { value: 1e12, name: "Trillion" },
  { value: 1e9, name: "Billion" },
  { value: 1e6, name: "Million" },
  { value: 1e3, name: "Thousand" },
  { value: 1e2, name: "Hundred" },
];

function numberToWords(num) {
  if (num < 20) return SMALL_NUMBERS[num];
  if (num < 100) {
    const ten = Math.floor(num / 10);
    const rest = num % 10;
    return TENS[ten] + (rest ? ` ${SMALL_NUMBERS[rest]}` : "");
  }
  for (let { value, name } of SCALE) {
    if (num >= value) {
      const lead = Math.floor(num / value);
      const rem = num % value;
      const leadWords = numberToWords(lead);
      return rem ? `${leadWords} ${name} ${numberToWords(rem)}` : `${leadWords} ${name}`;
    }
  }
  return ""; // should never hit
}

// export function amountToWords(amount) {
//   // 1) Get a clean string: strip commas
//   const raw = (typeof amount === 'string'
//     ? amount.replace(/,/g, '')
//     : String(amount)
//   ).trim();

//   // 2) Split integer vs. fractional
//   const [intStr, fracRaw = ''] = raw.split('.');

//   // parse integer part (default 0)
//   const integerPart = parseInt(intStr, 10) || 0;

//   // 3) Grab up to 4 digits of the fraction, then drop trailing zeros
//   let fracPart = fracRaw.slice(0, 4).replace(/0+$/, '');

//   // convert words for the integer part
//   const intWords = numberToWords(integerPart) || 'Zero';

//   // 4) If after trimming there's nothing left → no non-zero decimals
//   if (!fracPart) {
//     return `${intWords} Rupees Only`;
//   }

//   // 5) Otherwise parse & convert those digits
//   const fracNumber = parseInt(fracPart, 10);
//   const fracWords  = numberToWords(fracNumber);

//   return `${intWords} and ${fracWords} Paisas Only`;
// }
export function amountToWords(amount) {
  const raw = (typeof amount === "string" ? amount.replace(/,/g, "") : String(amount)).trim();

  const [intStr, fracRaw = ""] = raw.split(".");

  const integerPart = parseInt(intStr, 10) || 0;
  const intWords = numberToWords(integerPart) || "Zero";

  if (!fracRaw) {
    return `${intWords} Rupees Only`;
  }

  const fracSlice = fracRaw.slice(0, 4);
  const fracNumber = parseInt(fracSlice, 10) || 0;

  if (fracNumber === 0) {
    return `${intWords} Rupees Only`;
  }

  const fracWords = numberToWords(fracNumber);
  return `${intWords} and ${fracWords} Paisas Only`;
}

function floorTo4IfMore(n) {
  const s = n.toString().split(".");
  const dec = s[1] || "";
  if (dec.length > 4) {
    return Math.floor(n * 1e4) / 1e4;
  }
  return n;
}

export function separateNumber(
  value,
  {
    format = false,
    digits = 4,
    method = "round"
  } = {}
) {
  // 1) Handle N/A
  if (value === "N/A") {
    return { whole: "N/A", decimal: "", hasDecimal: false };
  }

  // 2) Normalize to string
  let rawStr = typeof value === "number" ? String(value) : value;
  if (typeof rawStr !== "string") {
    throw new Error(`separateNumberParts: unsupported type ${typeof rawStr}`);
  }
  rawStr = rawStr.trim().replace(/,/g, "");

  // 3) Detect original decimal
  const hadDecimal = rawStr.includes(".");

  // 4) Empty → zero
  if (rawStr === "") {
    return format
      ? {
          whole: "0",
          decimal: "0".repeat(digits),
          hasDecimal: digits > 0
        }
      : { whole: "0", decimal: "", hasDecimal: false };
  }

  // 5) Parse & validate
  const n = parseFloat(rawStr);
  if (!isFinite(n)) {
    throw new Error(`separateNumberParts: "${value}" is not a finite number`);
  }

  let whole, decimal;

  if (format) {
    // —— formatting path —— 
    const factor = 10 ** digits;
    let num;
    switch (method) {
      case "ceil":
        num = Math.ceil(n * factor) / factor;
        break;
      case "floor":
        num = Math.floor(n * factor) / factor;
        break;
      case "trunc":
        num = Math.trunc(n * factor) / factor;
        break;
      default:
        num = Math.round(n * factor) / factor;
    }
    // toFixed pads/truncates to exactly `digits` places
    const s = num.toFixed(digits);
    [whole, decimal = ""] = s.split(".");
  } else {
    // —— raw‐split path —— 
    [whole, decimal = ""] = rawStr.split(".");
    // preserve every original digit; no slicing
  }

  // 6) Decide hasDecimal
  const hasDecimal = format
    ? digits > 0
    : hadDecimal && decimal.length > 0;

  return { whole, decimal, hasDecimal };
}

export function separateNumberIfDecimal(
  value,
  {
    format = false,
    digits = 4,
    method = "round"
  } = {}
) {
  // 1) Handle N/A
  if (value === "N/A") {
    return { whole: "N/A", decimal: "", hasDecimal: false };
  }

  // 2) Normalize to string
  let rawStr = typeof value === "number" ? String(value) : value;
  if (typeof rawStr !== "string") {
    throw new Error(`separateNumberIfDecimal: unsupported type ${typeof rawStr}`);
  }
  rawStr = rawStr.trim().replace(/,/g, "");

  // 3) Empty → zero (preserve original behavior: formatting only when decimals exist)
  if (rawStr === "") {
    // no original decimal digits -> behave like no-format version
    return format
      ? {
          whole: "0",
          decimal: "0".repeat(Math.min(4, Math.max(0, Number(digits) || 0))),
          hasDecimal: (Math.min(4, Math.max(0, Number(digits) || 0))) > 0
        }
      : { whole: "0", decimal: "", hasDecimal: false };
  }

  // 4) Detect whether original had decimal digits (dot + at least one digit)
  const parts = rawStr.split(".");
  const origDecimal = parts[1] ?? "";
  const hadDecimalDigits = origDecimal.length > 0;

  // 5) Parse & validate numeric value
  const n = parseFloat(rawStr);
  if (!isFinite(n)) {
    throw new Error(`separateNumberIfDecimal: "${value}" is not a finite number`);
  }

  // 6) Cap digits to maximum 4 (user requested "up to 4 decimal points")
  const maxDigits = 4;
  digits = Math.min(maxDigits, Math.max(0, Math.floor(Number(digits) || 0)));

  let whole, decimal;

  // 7) Only apply formatting if user asked format AND the original input had decimal digits
  if (format && hadDecimalDigits) {
    const factor = 10 ** digits;
    let num;
    switch (method) {
      case "ceil":
        num = Math.ceil(n * factor) / factor;
        break;
      case "floor":
        num = Math.floor(n * factor) / factor;
        break;
      case "trunc":
        num = Math.trunc(n * factor) / factor;
        break;
      default:
        num = Math.round(n * factor) / factor;
    }

    // toFixed ensures we have up to `digits` places, then we trim trailing zeros
    const fixed = digits > 0 ? num.toFixed(digits) : String(Math.trunc(num));
    const s = fixed.replace(/(?:\.0+|(\.\d+?)0+)$/, "$1"); // remove trailing zeros, keep decimal if any sig digits remain
    [whole, decimal = ""] = s.split(".");
    // decimal now contains up to `digits` significant decimal digits (no unnecessary trailing zeros)
  } else {
    // —— raw‐split path —— (no formatting)
    [whole, decimal = ""] = parts;
    // preserve every original digit in decimal (including if it's empty)
  }

  // 8) Decide hasDecimal (true only if decimal part has at least one char)
  const hasDecimal = decimal.length > 0;

  return { whole, decimal, hasDecimal };
}


export function format4(n) {
  const rounded = Math.round(n * 1e4) / 1e4;

  const [intPart, decPart = ""] = String(rounded).split(".");

  const padded = (decPart + "0000").slice(0, 4);

  return `${intPart}.${padded}`;
}

export function format2(n) {
  const rounded = Math.ceil(n * 1e2) / 1e2;

  const [intPart, decPart = ""] = String(rounded).split(".");

  const padded = (decPart + "00").slice(0, 2);

  return `${intPart}.${padded}`;
}

export function handlePercentageInput(value, setter) {
  // Split into characters and filter only numbers and one decimal point
  const chars = value.split('');
  let cleanValue = '';
  let hasDecimal = false;
  
  chars.forEach(char => {
    if (char >= '0' && char <= '9') {
      cleanValue += char;
    } else if (char === '.' && !hasDecimal) {
      cleanValue += char;
      hasDecimal = true;
    }
  });
  
  setter(cleanValue);
};

// returns compact string for axis labels: "<1000" -> "950", "1200" -> "1.2K", "1500000" -> "1.5M"
export function formatCompactNumber(n) {
  if (n == null || !isFinite(n)) return "—";
  const abs = Math.abs(n);

  // small numbers: show full with commas
  if (abs < 1_000) {
    return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  // thousands
  if (abs < 1_000_000) {
    const val = n / 1_000;
    // show one decimal only when needed (e.g., 1.2K), else integer (e.g., 12K)
    const formatted = val >= 10 ? Math.round(val).toString() : (Math.round(val * 10) / 10).toString();
    return `${formatted}K`;
  }

  // millions
  if (abs < 1_000_000_000) {
    const val = n / 1_000_000;
    const formatted = val >= 10 ? Math.round(val).toString() : (Math.round(val * 10) / 10).toString();
    return `${formatted}M`;
  }

  // billions and above
  const val = n / 1_000_000_000;
  const formatted = val >= 10 ? Math.round(val).toString() : (Math.round(val * 10) / 10).toString();
  return `${formatted}B`;
}

// money.js - Copy this single file to all your microservices
export const MONEY = {
  DECIMALS: 4,
  MULTIPLIER: 10000,
  
  from(value) {
    if (typeof value === 'string') return Math.round(parseFloat(value) * this.MULTIPLIER);
    if (typeof value === 'number') return Math.round(value * this.MULTIPLIER);
    return value;
  },
  
  // Safe arithmetic
  add(a, b) { return a + b; },
  subtract(a, b) { return a - b; },
  multiply(a, b) { return Math.round((a * b) / this.MULTIPLIER); },
  divide(a, b) { return Math.round((a / b) * this.MULTIPLIER); },
  
  toString(value) { return (value / this.MULTIPLIER).toFixed(this.DECIMALS); },
  toNumber(value) { return value / this.MULTIPLIER; }
};

// // usage in your microservices
// const navPrice = MONEY.from("15.1234");      // 151234
// const units = MONEY.from("123.5678");        // 1235678

// const redemptionValue = MONEY.multiply(navPrice, units);
// console.log(MONEY.toString(redemptionValue)); // "1868.7234"